import React, { useMemo } from 'react';
import type DecisionType from '@modules/product/type/decision-type';
import type Attribute from '@modules/product/type/attribute';
import type AttributeOption from '@modules/product/type/attribute-option';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import { useLandingVariant } from '../hook/use-landing-variant';

interface AttributeWithOptionsType extends Attribute {
    options: AttributeOption[];
    isGeneric: boolean;
}

interface VariantOptionProviderProps {
    product: ProductPageLoaderData['product'];
    children: React.ReactNode | React.ReactNode[];
}

interface VariantOptionState {
    decision: DecisionType;
    setDecision(d: DecisionType): void;
    attributeMap: Map<number, AttributeWithOptionsType>;
    attributeOptionMap: Map<number, Attribute>;
    /**
     * Maps every option ID to its canonical twin — the first option sharing
     * the same Attribute + Label (duplicate CMS records). Selection state and
     * variant matching compare through this map so display-level dedup never
     * makes a variant unreachable or a swatch unhighlightable. For products
     * without duplicates every ID maps to itself.
     */
    canonicalOptionIds: Map<number, number>;
}

const VariantOptionContext = React.createContext<
    VariantOptionState | undefined
>(undefined);

const useVariantOptionState = (): VariantOptionState => {
    const context = React.useContext(VariantOptionContext);
    if (!context)
        throw new Error('Cannot be used outside VariantOptionContext');
    return context;
};

const VariantOptionProvider = ({
    product,
    children,
}: VariantOptionProviderProps) => {
    const landingVariant = useLandingVariant(product);

    const optionMaps = React.useMemo(() => {
        const attributeOptions = product.AttributeOptions ?? [];

        // B2: detect duplicate AttributeOption records sharing the same
        // Attribute + Label (different IDs, same visual meaning). The first
        // occurrence becomes the canonical ID; every option ID maps to its
        // canonical so selection state and matching can compare canonically.
        const labelKeyToCanonical = new Map<string, number>();
        const optionIdToCanonical = new Map<number, number>();

        for (const opt of attributeOptions) {
            if (!opt.Attribute) continue;
            // Normalise: Attribute ID + lower-cased label, falling back to
            // Code when Label is absent (mirrors the spec's Attribute+Label+Code rule).
            const labelKey = `${opt.Attribute.ID}:${(opt.Label ?? opt.Code ?? '').toLowerCase().trim()}`;
            const existing = labelKeyToCanonical.get(labelKey);
            if (existing !== undefined) {
                optionIdToCanonical.set(opt.ID, existing);
            } else {
                labelKeyToCanonical.set(labelKey, opt.ID);
                optionIdToCanonical.set(opt.ID, opt.ID);
            }
        }

        const attributeMap = attributeOptions.reduce((map, opt) => {
            // Skip orphaned options whose Attribute no longer exists.
            if (!opt.Attribute) return map;
            const attr =
                map.get(opt.Attribute.ID) ||
                ({
                    ...opt.Attribute,
                    options: [],
                    isGeneric: false,
                } as AttributeWithOptionsType);
            // B2: only push the option if it is the canonical (first) occurrence
            // for this Attribute+Label — duplicates with the same label are
            // omitted from display so swatches/chips don't appear twice.
            const canonicalId = optionIdToCanonical.get(opt.ID);
            if (canonicalId === opt.ID) {
                attr.options.push(opt);
            }
            return map.set(attr.ID, attr);
        }, new Map<number, AttributeWithOptionsType>());

        // attributeOptionMap must include ALL option IDs (including duplicates)
        // so variant-decision matching continues to resolve decisions derived
        // from landing variants that reference a non-canonical ID.
        const attributeOptionMap = attributeOptions.reduce(
            (map, opt) =>
                opt.Attribute ? map.set(opt.ID, opt.Attribute) : map,
            new Map<number, Attribute>(),
        );

        for (const [key, value] of attributeMap) {
            value.isGeneric = product.Variants.every((v) =>
                v.Options.some((opt) => opt.AttributeID === key),
            );
        }

        return {
            attributeMap,
            attributeOptionMap,
            canonicalOptionIds: optionIdToCanonical,
        };
    }, [product]);

    const [decision, setDecision] = React.useState<DecisionType>({
        options: landingVariant
            ? landingVariant.Options.reduce(
                  (decision, opt) => {
                      const attribute = optionMaps.attributeOptionMap.get(
                          opt.ID,
                      );
                      // Store the canonical twin so the decision always
                      // matches the deduped display options.
                      if (attribute) {
                          decision[attribute.ID] =
                              optionMaps.canonicalOptionIds.get(opt.ID) ??
                              opt.ID;
                      }
                      return decision;
                  },
                  {} as DecisionType['options'],
              )
            : {},
        size: landingVariant?.Size?.ID ?? null,
    });

    const value = useMemo(
        () => ({
            decision,
            setDecision,
            ...optionMaps,
        }),
        [optionMaps, decision, setDecision],
    );

    return (
        <VariantOptionContext.Provider value={value}>
            {children}
        </VariantOptionContext.Provider>
    );
};

export { VariantOptionProvider, useVariantOptionState };
